/**
 * RenewalCard — per-policy card on the Renewals board.
 * Ported from v0 components/brokly/renewal-card.tsx (CardShell + RenewalCard),
 * adapted to the R2 wire contract (RenewalBoardItemVM).
 *
 * Colour discipline (design.md §2; UI-U3 AC 7):
 *   - Urgency status renders as a StatusDot (dot + colored text), NEVER a filled
 *     pill — informieren = warning, mahnen = danger, resting = neutral (no status).
 *   - The FaelligText countdown carries the same urgency colour.
 *   - All chrome (Sparte, police number, Versicherer, dates, Prämie) stays neutral.
 *   - EntityLink carries sem-info (standard app-wide link affordance — NOT urgency).
 *
 * [Source: docs/concept/2026-06-26_post-stefan-roadmap-and-module-specs.md §41 (R3)]
 */

import { Building2 } from 'lucide-react';
import { StatusDot } from '../../../components/ui/StatusDot';
import { EntityLink } from '../../../components/ui/EntityLink';
import { SparteBadge } from '../../entity-detail/components/SparteBadge';
import { FaelligText } from '../../entity-detail/components/FaelligText';
import { chf } from '../../../lib/format';
import { STATUS_META } from '../mock';
import type { RenewalBoardItemVM } from '../types';

// ── Date formatter ───────────────────────────────────────────────────────────
// Manual split so tests are stable regardless of Node.js ICU locale data.
export function fmtDate(iso: string | null): string {
  if (!iso) return '-';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
}

// ── Card ─────────────────────────────────────────────────────────────────────

interface RenewalCardProps {
  item: RenewalBoardItemVM;
  onKundeActivate?: () => void;
}

export function RenewalCard({ item, onKundeActivate }: RenewalCardProps) {
  const statusMeta = STATUS_META[item.status] ?? null;
  const praemieNum = item.praemie !== null ? Number(item.praemie) : null;

  return (
    <div className="flex w-full flex-col gap-2.5 rounded-xl border border-border/60 bg-card p-3 text-left shadow-none transition-colors hover:border-border">
      {/* Row 1: Sparte (neutral) + urgency status (dot + text, never a filled pill) */}
      <div className="flex items-start justify-between gap-2">
        {item.sparte ? (
          <SparteBadge sparte={item.sparte} />
        ) : (
          <span className="inline-flex h-5 items-center rounded-md border border-border bg-background px-1.5 text-xs text-muted-foreground">
            -
          </span>
        )}
        {/* Urgency = dot + colored text — the ONLY coloured element (besides FaelligText below) */}
        {statusMeta !== null && (
          <span className="text-xs font-medium">
            <StatusDot tone={statusMeta.tone} label={statusMeta.label} />
          </span>
        )}
      </div>

      {/* Row 2: Policy number (neutral mono) */}
      <span className="mono-num text-sm font-medium text-foreground">{item.policy_number}</span>

      {/* Row 3: Kunde · Versicherer */}
      <div className="flex flex-wrap items-center gap-1 text-sm">
        {item.kundeId ? (
          <EntityLink onActivate={onKundeActivate}>
            {item.kunde ?? '-'}
          </EntityLink>
        ) : (
          <span className="text-sm text-foreground">{item.kunde ?? '-'}</span>
        )}
        <span className="text-muted-foreground">·</span>
        <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
          <Building2 className="size-3 shrink-0" aria-hidden />
          {item.versicherer ?? '-'}
        </span>
      </div>

      {/* Row 4: renewalDate + actByDate (neutral, NEW vs v0) */}
      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground [font-variant-numeric:tabular-nums]">
        <span>
          <span className="font-medium">Ablauf:</span>{' '}
          {fmtDate(item.renewal_date)}
        </span>
        <span>
          <span className="font-medium">Frist:</span>{' '}
          {fmtDate(item.act_by_date)}
        </span>
      </div>

      {/* Row 5: days_left countdown (urgency-coloured) + Prämie (neutral) */}
      <div className="flex items-center justify-between gap-2">
        {item.days_left !== null ? (
          <FaelligText restTage={item.days_left} />
        ) : (
          <span className="text-sm text-muted-foreground">Frist offen</span>
        )}
        <span className="text-sm font-semibold mono-num text-foreground">
          {praemieNum !== null ? chf(praemieNum) : '-'}
        </span>
      </div>
    </div>
  );
}
